import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  Input,
  Stack,
  Typography,
} from "@mui/joy";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../../lib/api";
import { EmailPreview } from "./EmailPreview";
import {
  emailError,
  timezoneName,
  type AudiencePreview,
} from "./emailWorkflows";
export function CampaignReview({
  id,
  action = "send-now",
  scheduledAt = "",
  onComplete,
  onBack,
  draftLabel = "Close review",
}: {
  id: number;
  action?: "send-now" | "resume" | "schedule" | "draft";
  scheduledAt?: string;
  onComplete: () => void;
  onBack?: () => void;
  draftLabel?: string;
}) {
  const [confirmed, setConfirmed] = useState(false);
  const [time, setTime] = useState(scheduledAt);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const cache = useQueryClient();
  const preview = useQuery({
    queryKey: ["email-review", id],
    queryFn: async () =>
      (
        await api.get<AudiencePreview>(
          `/api/marketing/email-campaigns/${id}/recipients-preview/`,
        )
      ).data,
    staleTime: 0,
  });
  const run = async () => {
    if (action === "draft") {
      onComplete();
      return;
    }
    setBusy(true);
    setError("");
    try {
      await api.post(
        `/api/marketing/email-campaigns/${id}/${action}/`,
        action === "schedule"
          ? {
              scheduled_at: new Date(time).toISOString(),
              review_token: preview.data?.review_token,
            }
          : { review_token: preview.data?.review_token },
      );
      await cache.invalidateQueries({ queryKey: ["email-campaigns"] });
      await cache.invalidateQueries({ queryKey: ["email-quota"] });
      onComplete();
    } catch (cause) {
      setError(emailError(cause));
    }
    setBusy(false);
  };
  const data = preview.data;
  return (
    <Stack spacing={2}>
      {error && (
        <Alert
          color="danger"
          endDecorator={
            <Button
              onClick={() => {
                setConfirmed(false);
                void preview.refetch();
              }}
            >
              Refresh review
            </Button>
          }
        >
          {error}
        </Alert>
      )}
      {preview.isPending && (
        <Typography>Checking the saved campaign and audience…</Typography>
      )}
      {preview.isError && (
        <Alert
          color="danger"
          endDecorator={
            <Button onClick={() => preview.refetch()}>Retry</Button>
          }
        >
          Review could not be loaded.
        </Alert>
      )}
      {data && (
        <>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4,1fr)" },
              gap: 1,
            }}
          >
            {[
              ["Eligible", data.count],
              ["Already accepted", data.accepted_count],
              ["Outstanding", data.outstanding_count],
              ["Excluded", data.excluded_count],
            ].map(([label, count]) => (
              <Box
                key={label}
                sx={{
                  p: 1.5,
                  bgcolor: "background.level1",
                  borderRadius: "sm",
                }}
              >
                <Typography level="h3">{count}</Typography>
                <Typography level="body-sm">{label}</Typography>
              </Box>
            ))}
          </Box>
          <Typography level="body-sm">
            {data.quota.remaining} quota slots available. Up to{" "}
            {Math.min(data.outstanding_count, data.quota.remaining)} outstanding
            messages can be accepted within the current allowance.{" "}
            {data.quota.reserved || 0} slots are held by in-flight or uncertain
            requests.
          </Typography>
          {data.held_count > 0 && (
            <Alert color="warning">
              {data.held_count} uncertain or in-flight requests will not be
              resent. Resolve them in the campaign report.
            </Alert>
          )}
          {data.outstanding_count > data.quota.remaining && (
            <Alert color="warning">
              The current quota cannot cover all outstanding recipients.
              Delivery will pause at the limit; remaining recipients can be
              retried later.
            </Alert>
          )}
          {!!data.excluded_count && (
            <Box>
              <Typography level="title-sm">Exclusions</Typography>
              {Object.entries(data.exclusion_reasons).map(([reason, count]) => (
                <Typography key={reason} level="body-sm">
                  {reason}: {count}
                </Typography>
              ))}
            </Box>
          )}
          <Box component="details">
            <Box component="summary" sx={{ cursor: "pointer" }}>
              Review recipient sample ({data.sample.length} of {data.count})
            </Box>
            {data.sample.map((contact) => (
              <Typography
                key={contact.id}
                level="body-sm"
                sx={{ overflowWrap: "anywhere" }}
              >
                {contact.name} · {contact.email} · {contact.reason}
              </Typography>
            ))}
            {data.excluded_sample.map((contact) => (
              <Typography key={`excluded-${contact.id}`} level="body-sm">
                Excluded: {contact.name} · {contact.reason}
              </Typography>
            ))}
          </Box>
          {data.content.frozen_at && (
            <Typography level="body-sm">
              Content and audience snapshot saved{" "}
              {new Date(data.content.frozen_at).toLocaleString()}. Opt-outs and
              changed addresses are rechecked before dispatch.
            </Typography>
          )}
          <EmailPreview
            html={data.content.html}
            subject={data.content.subject}
            preheader={data.content.preview_text}
          />
          {action === "schedule" && (
            <>
              <FormControl>
                <FormLabel>Send at ({timezoneName})</FormLabel>
                <Input
                  type="datetime-local"
                  value={time}
                  disabled={busy}
                  onChange={(event) => {
                    setTime(event.target.value);
                    setConfirmed(false);
                  }}
                />
              </FormControl>
              {time && !Number.isNaN(new Date(time).valueOf()) && (
                <Typography level="body-sm">
                  Scheduled instant: {new Date(time).toLocaleString()} (
                  {timezoneName})
                </Typography>
              )}
            </>
          )}
          {action !== "draft" && (
            <Checkbox
              checked={confirmed}
              onChange={(event) => setConfirmed(event.target.checked)}
              label={`I reviewed the content, recipients, exclusions, and quota and approve ${action === "schedule" ? "this scheduled campaign" : "sending these emails"}.`}
            />
          )}
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            {onBack && (
              <Button variant="plain" disabled={busy} onClick={onBack}>
                Back
              </Button>
            )}
            <Button
              loading={busy}
              disabled={
                action !== "draft" &&
                (!confirmed ||
                  !data.outstanding_count ||
                  (action !== "schedule" && data.quota.remaining <= 0) ||
                  (action === "schedule" &&
                    (!time ||
                      Number.isNaN(new Date(time).valueOf()) ||
                      new Date(time) <= new Date())))
              }
              onClick={run}
            >
              {action === "draft"
                ? draftLabel
                : action === "schedule"
                  ? "Confirm schedule"
                  : action === "resume"
                    ? "Confirm resume"
                    : "Confirm send"}
            </Button>
          </Stack>
        </>
      )}
    </Stack>
  );
}
